import { computed, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withMethods,
  withProps,
} from '@ngrx/signals';
import {
  entityConfig,
  removeAllEntities,
  removeEntity,
  setAllEntities,
  setEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { withCalls, withEntitiesCalls, withLogger } from '@ngrx-traits/signals';
import { callConfig } from '@ngrx-traits/signals';
import { entityCallConfig } from '@ngrx-traits/signals';
import { withEntitiesLocalSort } from '@ngrx-traits/signals';
import { ViewConfiguratorService } from '../data/view-configurator.service';
import { UiLayoutGroupDto } from '../models/ui-layout-group.dto';
import { UiLayoutFieldDto } from '../models/ui-layout-field.dto';
import { map } from 'rxjs';
import { caseFieldRegistry } from '@app/features/case-data/case-field-registry';

const GRID_COLUMNS = 3;

function normalizeFieldPositions(
  fields: UiLayoutFieldDto[]
): UiLayoutFieldDto[] {
  const fieldsByGroup = new Map<number, UiLayoutFieldDto[]>();
  fields.forEach((field) => {
    const groupFields = fieldsByGroup.get(field.uiLayoutGroupId) ?? [];
    groupFields.push(field);
    fieldsByGroup.set(field.uiLayoutGroupId, groupFields);
  });

  const normalizedFields: UiLayoutFieldDto[] = [];

  fieldsByGroup.forEach((groupFields) => {
    const occupiedPositions = new Set<string>();
    const fieldsToNormalize: UiLayoutFieldDto[] = [];

    groupFields.forEach((field) => {
      const key = `${field.row}-${field.column}`;
      if (occupiedPositions.has(key)) {
        fieldsToNormalize.push(field);
      } else {
        occupiedPositions.add(key);
        normalizedFields.push(field);
      }
    });

    fieldsToNormalize.forEach((field) => {
      let row = 0;
      let col = 0;

      while (occupiedPositions.has(`${row}-${col}`)) {
        col++;
        if (col >= GRID_COLUMNS) {
          col = 0;
          row++;
        }
      }

      occupiedPositions.add(`${row}-${col}`);
      normalizedFields.push({
        ...field,
        row,
        column: col,
      });
    });
  });

  return normalizedFields;
}

const groupsEntityConfig = entityConfig({
  entity: type<UiLayoutGroupDto>(),
  collection: 'groups',
  selectId: (group: UiLayoutGroupDto) => String(group.id),
});

const fieldsEntityConfig = entityConfig({
  entity: type<UiLayoutFieldDto>(),
  collection: 'fields',
  selectId: (field: UiLayoutFieldDto) => String(field.id),
});

export const ViewLayoutStore = signalStore(
  withEntities(groupsEntityConfig),
  withEntitiesLocalSort({
    entity: type<UiLayoutGroupDto>(),
    collection: 'groups',
    defaultSort: { field: 'order', direction: 'asc' },
  }),

  withEntities(fieldsEntityConfig),
  withEntitiesLocalSort({
    entity: type<UiLayoutFieldDto>(),
    collection: 'fields',
    defaultSort: { field: 'row', direction: 'asc' },
    sortFunction: (fields) => {
      return [...fields].sort((a, b) => {
        const rowCompare = a.row - b.row;
        if (rowCompare !== 0) return rowCompare;
        return a.column - b.column;
      });
    },
  }),

  withLogger({
    name: 'ViewLayoutStore',
    showDiff: true,
  }),

  withProps(() => ({
    _viewConfiguratorService: inject(ViewConfiguratorService),
    _snackBar: inject(MatSnackBar),
  })),

  withComputed((state) => ({
    groupsCount: computed(() => state.groupsIds().length),
    hasGroups: computed(() => state.groupsIds().length > 0),
    fieldsCount: computed(() => state.fieldsIds().length),
    hasFields: computed(() => state.fieldsIds().length > 0),
    layout: computed(() => ({
      groups: state.groupsEntities(),
      fields: state.fieldsEntities(),
    })),
  })),

  withCalls(({ _viewConfiguratorService, ...store }) => ({
    loadLayout: callConfig({
      call: ({
        viewId,
        transformToFormModel = false,
      }: {
        viewId: number;
        transformToFormModel?: boolean;
      }) =>
        _viewConfiguratorService.getGroupsWithFields(viewId).pipe(
          map((groupsWithFields) => {
            const allFields = groupsWithFields
              .flatMap((g) => g.uiLayoutFields ?? [])
              .map((field) => {
                // Normalize legacy form model paths to API format
                const normalizedPath = caseFieldRegistry.normalizeToApiPath(
                  field.path
                );

                // Optionally transform API paths to form model paths for display
                const finalPath = transformToFormModel
                  ? caseFieldRegistry.apiPathToFormModelPath(normalizedPath)
                  : normalizedPath;

                return { ...field, path: finalPath };
              });

            const normalizedFields = normalizeFieldPositions(allFields);

            return { groupsWithFields, allFields: normalizedFields };
          })
        ),
      onSuccess: ({ groupsWithFields, allFields }) => {
        patchState(store, setAllEntities(groupsWithFields, groupsEntityConfig));
        patchState(store, setAllEntities(allFields, fieldsEntityConfig));
      },
    }),

    createGroup: callConfig({
      call: ({
        viewId,
        title,
        order,
      }: {
        viewId: number;
        title?: string;
        order: number;
      }) =>
        _viewConfiguratorService.createGroup(viewId, {
          uiViewId: viewId,
          title,
          order,
        }),
      onSuccess: (newGroup) => {
        patchState(store, setEntity(newGroup, groupsEntityConfig));
        store._snackBar.open('Gruppe erstellt', 'OK', { duration: 2000 });
      },
    }),

    createField: callConfig({
      call: ({
        viewId,
        groupId,
        path,
        label,
        row,
        col,
      }: {
        viewId: number;
        groupId: number;
        path: string;
        label?: string;
        row: number;
        col: number;
      }) => {
        // Always convert to API path format for storage
        const apiPath = caseFieldRegistry.formModelPathToApiPath(path);
        return _viewConfiguratorService.createField(viewId, groupId, {
          uiGroupId: groupId,
          path: apiPath,
          label,
          row,
          col,
          isVisible: true,
        });
      },
      onSuccess: (newField) => {
        // Store field with API path (as returned from server)
        patchState(store, setEntity(newField, fieldsEntityConfig));
        store._snackBar.open('Feld hinzugefügt', 'OK', { duration: 2000 });
      },
    }),
  })),

  withEntitiesCalls({
    ...groupsEntityConfig,
    calls: ({
      _viewConfiguratorService,
      _snackBar,
      sortGroupsEntities,
      ...store
    }) => ({
      updateGroup: entityCallConfig({
        paramsSelectId: (params: {
          viewId: number;
          id: number;
          title?: string;
          order?: number;
        }) => String(params.id),
        call: (params: {
          viewId: number;
          id: number;
          title?: string;
          order?: number;
        }) =>
          _viewConfiguratorService
            .updateGroup(params.viewId, params.id, {
              title: params.title,
              order: params.order,
            })
            .pipe(
              map(() => ({
                ...(params.title !== undefined && { title: params.title }),
                ...(params.order !== undefined && { order: params.order }),
              }))
            ),
        onSuccess: () => {
          sortGroupsEntities();
          _snackBar.open('Gruppe aktualisiert', 'OK', { duration: 2000 });
        },
      }),

      deleteGroup: entityCallConfig({
        paramsSelectId: (params: { viewId: number; id: number }) =>
          String(params.id),
        call: (params: { viewId: number; id: number }) =>
          _viewConfiguratorService
            .deleteGroup(params.viewId, params.id)
            .pipe(map(() => undefined)),
        onSuccess: (_void, params) => {
          const fieldIdsToRemove = store.fieldsIds().filter((id) => {
            const field = store.fieldsEntityMap()[id];
            return field.uiLayoutGroupId === params.id;
          });
          fieldIdsToRemove.forEach((fieldId) => {
            patchState(store, removeEntity(fieldId, fieldsEntityConfig));
          });
          _snackBar.open('Gruppe gelöscht', 'OK', { duration: 2000 });
        },
      }),
    }),
  }),

  withEntitiesCalls({
    ...fieldsEntityConfig,
    calls: ({ _viewConfiguratorService, _snackBar, sortFieldsEntities }) => ({
      updateField: entityCallConfig({
        paramsSelectId: (params: {
          viewId: number;
          groupId: number;
          id: number;
          uiLayoutGroupId?: number;
          label?: string;
          row?: number;
          column?: number;
          isVisible?: boolean;
          isReadOnly?: boolean;
        }) => String(params.id),
        call: (params: {
          viewId: number;
          groupId: number;
          id: number;
          uiLayoutGroupId?: number;
          label?: string;
          row?: number;
          column?: number;
          isVisible?: boolean;
          isReadOnly?: boolean;
        }) =>
          _viewConfiguratorService
            .updateField(params.viewId, params.groupId, params.id, {
              uiGroupId: params.uiLayoutGroupId,
              label: params.label,
              row: params.row,
              column: params.column,
              isVisible: params.isVisible,
              isReadOnly: params.isReadOnly,
            })
            .pipe(
              map(() => ({
                ...(params.uiLayoutGroupId !== undefined && {
                  uiLayoutGroupId: params.uiLayoutGroupId,
                }),
                ...(params.label !== undefined && { label: params.label }),
                ...(params.row !== undefined && { row: params.row }),
                ...(params.column !== undefined && { column: params.column }),
                ...(params.isVisible !== undefined && {
                  isVisible: params.isVisible,
                }),
                ...(params.isReadOnly !== undefined && {
                  isReadOnly: params.isReadOnly,
                }),
              }))
            ),
        onSuccess: () => {
          sortFieldsEntities();
          _snackBar.open('Feld aktualisiert', 'OK', { duration: 2000 });
        },
      }),

      deleteField: entityCallConfig({
        paramsSelectId: (params: {
          viewId: number;
          groupId: number;
          id: number;
        }) => String(params.id),
        call: (params: { viewId: number; groupId: number; id: number }) =>
          _viewConfiguratorService
            .deleteField(params.viewId, params.groupId, params.id)
            .pipe(map(() => undefined)),
        onSuccess: () => {
          sortFieldsEntities();
          _snackBar.open('Feld entfernt', 'OK', { duration: 2000 });
        },
      }),
    }),
  }),

  withMethods((store) => ({
    getGroupById(id: number): UiLayoutGroupDto | undefined {
      return store.groupsEntityMap()[String(id)];
    },

    hasGroup(id: number): boolean {
      return store.groupsIds().includes(String(id));
    },

    clearGroups(): void {
      patchState(store, removeAllEntities(groupsEntityConfig));
    },

    getFieldById(id: number): UiLayoutFieldDto | undefined {
      return store.fieldsEntityMap()[String(id)];
    },

    getFieldsForGroup(groupId: number): UiLayoutFieldDto[] {
      return store
        .fieldsEntities()
        .filter((field) => field.uiLayoutGroupId === groupId)
        .sort((a, b) => {
          if (a.row !== b.row) {
            return a.row - b.row;
          }
          return a.column - b.column;
        });
    },

    hasField(id: number): boolean {
      return store.fieldsIds().includes(String(id));
    },

    clearFields(): void {
      patchState(store, removeAllEntities(fieldsEntityConfig));
    },

    removeFieldsForGroup(groupId: number): void {
      const fieldIdsToRemove = store.fieldsIds().filter((id) => {
        const field = store.fieldsEntityMap()[id];
        return field.uiLayoutGroupId === groupId;
      });

      fieldIdsToRemove.forEach((fieldId) => {
        patchState(store, removeEntity(fieldId, fieldsEntityConfig));
      });
    },

    clearLayout(): void {
      patchState(store, removeAllEntities(groupsEntityConfig));
      patchState(store, removeAllEntities(fieldsEntityConfig));
    },
  }))
);
